// Praat met de Django-API op dezelfde oorsprong. Wijzigingen sturen de CSRF-token mee (cookie csrftoken).
// Niet aangemeld: naar /auth/login, daarna terug naar dezelfde pagina.
import type { Me, NewTask, OpsResult, PlanningData, Proposal, TaskOp } from "./types";

export class ApiError extends Error {
  constructor(public status: number, message: string, public reauth = false) { super(message); }
}

function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

// Na Afmelden niet meer automatisch naar de login: anders meldt een verversing op de achtergrond u meteen opnieuw aan.
let loggedOut = false;
export function markLoggedOut() { loggedOut = true; }

function toLogin() {
  if (loggedOut) return;
  window.location.href = "/auth/login?next=" + encodeURIComponent(window.location.pathname + window.location.search);
}

export async function call<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json", "X-CSRFToken": csrf() } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  // ook reauth: de toestemming om namens u in Planner te schrijven is verlopen, opnieuw aanmelden lost dat op
  if (r.status === 401) { toLogin(); throw new ApiError(r.status, "niet aangemeld", !!j.reauth); }
  if (!r.ok) throw new ApiError(r.status, j.error || j.detail || "Er ging iets mis (" + r.status + ")", !!j.reauth);
  return j as T;
}

/** Een bestand naar de server, die het in SharePoint zet en aan de taak hangt; `onProgress` volgt het opladen (0 tot 1). */
export function upload(taskId: string, file: File, onProgress?: (frac: number) => void): Promise<OpsResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/tasks/${taskId}/bijlagen`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("X-CSRFToken", csrf());
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    xhr.onload = () => {
      let j: Partial<OpsResult> & { error?: string; detail?: string; reauth?: boolean } = {};
      try { j = JSON.parse(xhr.responseText); } catch { /* geen JSON, bv. een 413 van nginx */ }
      if (xhr.status === 401) { toLogin(); reject(new ApiError(xhr.status, "niet aangemeld", !!j.reauth)); return; }
      if (xhr.status === 413) { reject(new ApiError(xhr.status, j.error || "Het bestand is groter dan 100 MB.")); return; }
      if (xhr.status < 200 || xhr.status >= 300) { reject(new ApiError(xhr.status, j.error || j.detail || "Er ging iets mis (" + xhr.status + ")", !!j.reauth)); return; }
      resolve(j as OpsResult);
    };
    xhr.onerror = () => reject(new ApiError(0, "Geen verbinding met de server."));
    const fd = new FormData();
    fd.append("file", file, file.name);
    xhr.send(fd);
  });
}

export const api = {
  me: () => call<Me>("GET", "/api/me"),
  data: (fresh = false) => call<PlanningData>("GET", "/api/data" + (fresh ? "?fresh=1" : "")),
  ops: (taskId: string, ops: TaskOp[]) => call<OpsResult>("POST", `/api/tasks/${taskId}/ops`, { ops }),
  undo: (changeId: number) => call<OpsResult>("POST", `/api/changes/${changeId}/undo`, {}),
  createTask: (t: NewTask) => call<OpsResult>("POST", "/api/tasks", t),
  proposals: () => call<Proposal[]>("GET", "/api/proposals"),
  propose: (taskId: string, end: string) => call<{ ok: boolean; changeId: number }>("POST", "/api/proposals", { taskId, end }),
  logout: () => call<{ ok: boolean }>("POST", "/auth/logout", {}),
  upload,
};
